import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosRequestHeaders } from 'axios'
import type { RuntimeConfig } from '@/infra/env/getEnv'
import { authPort } from '@/infra/config/beans'
import i18n from 'i18next'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogIsEnabled } from '@/datadog-initialization.config'

let httpClient: AxiosInstance

const ensureHeaders = (config: AxiosRequestConfig): AxiosRequestHeaders => {
  config.headers ??= {} as AxiosRequestHeaders
  return config.headers as AxiosRequestHeaders
}

export const createHttpClient = (cfg: RuntimeConfig): AxiosInstance => {
  const client = axios.create({
    baseURL: cfg.BACKEND_URL,
    withCredentials: false,
    timeout: 15_000,
  })

  client.interceptors.request.use(async (config) => {
    const user = await authPort.getCurrentUser()
    const token = user?.accessToken
    if (token) {
      const headers = ensureHeaders(config)
      headers.Authorization = `Bearer ${token}`
      headers['Accept-Language'] = i18n.language || 'en'
    }
    return config
  })

  client.interceptors.response.use(
    (res) => res,
    async (err) => {
      if (datadogIsEnabled(cfg)) {
        datadogLogs.logger.error(`@instore-fcs-front error :${err.message}`, {
          status: err?.response?.status,
          url: err?.config?.url,
          method: err?.config?.method,
          data: err?.response?.data,
        })
      }

      const status = err?.response?.status
      const config = err?.config as (AxiosRequestConfig & { _refreshTried?: boolean }) | undefined
      if (status !== 401 || !config?.headers) throw err

      if (config._refreshTried) {
        await authPort.logout().catch(() => {})
        if (typeof window !== 'undefined') window.location.replace('/')
        throw err
      }

      config._refreshTried = true
      try {
        const newToken = await authPort.trySilentRefresh()
        if (newToken) {
          config.headers.Authorization = `Bearer ${newToken}`
          return client(config)
        }
      } catch {
        console.error('Silent refresh failed')
      }

      await authPort.logout().catch(() => {})
      if (typeof window !== 'undefined') window.location.replace('/')
      throw err
    }
  )

  return client
}

export const setHttpClient = (client: AxiosInstance) => {
  httpClient = client
}

export { httpClient }
